import { useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "../hooks/useInView";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

type AssemblingHeadlineProps = {
  lines: string[];
  className?: string;
  srOnly?: string;
  /** Etykieta podpowiedzi pod nagłówkiem. */
  hint?: string;
  /** Etykieta przycisku ponownego odtworzenia animacji. */
  replayLabel?: string;
};

/** Deterministyczny pseudo-random, żeby SSR/HMR nie skakało. */
function rand(seed: number) {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

/** Zasięg (px), w którym kursor przyciąga litery. */
const FIELD = 170;
/** Maksymalne wychylenie litery ku kursorowi. */
const PULL = 26;
/** Czas lotu odczepionej litery po orbicie 3D (ms). */
const ORBIT_MS = 2600;

type Orbit = {
  t0: number;
  /** Punkt odczepienia (offset px) - stąd litera startuje w kosmos. */
  sx: number;
  sy: number;
  rx: number;
  ry: number;
  rz: number;
  depth: number;
  spin: number;
  dir: number;
};

/**
 * Nagłówek hero: litery wlatują pojedynczo z losowych kierunków i scalają się
 * w nazwę firmy, a po złożeniu reagują na kursor efektem przyciągania
 * (magnetyzm pola). Reduced motion -> statyczny nagłówek.
 */
export function AssemblingHeadline({ lines, className, srOnly, hint, replayLabel }: AssemblingHeadlineProps) {
  const ref = useRef<HTMLHeadingElement>(null);
  // once: false -> po wyjściu z widoku i powrocie animacja gra od nowa
  const inView = useInView(ref as React.RefObject<Element>, { once: false, margin: "0px 0px -12% 0px" });
  const reducedMotion = usePrefersReducedMotion();
  const visible = useMemo(
    () => lines.filter((l) => l != null && String(l).trim() !== ""),
    [lines],
  );

  const [assembled, setAssembled] = useState(false);
  const [settled, setSettled] = useState(false);
  const [runKey, setRunKey] = useState(0);
  const letterRefs = useRef<Map<string, HTMLSpanElement>>(new Map());
  const rafRef = useRef<number | null>(null);
  const pointer = useRef<{ x: number; y: number } | null>(null);
  /** Trwale odczepione litery (klucz -> offset w px). */
  const offsets = useRef<Map<string, { x: number; y: number }>>(new Map());
  /** Litery lecące po orbicie 3D po puszczeniu. */
  const orbits = useRef<Map<string, Orbit>>(new Map());
  const returnTimers = useRef<Map<string, number>>(new Map());
  const dragRef = useRef<{ key: string; px: number; py: number; ox: number; oy: number } | null>(null);


  /** Czyści transformy nakładane przez pole magnetyczne i orbity. */
  const clearMagnetics = () => {
    dragRef.current = null;
    offsets.current.clear();
    orbits.current.clear();
    returnTimers.current.forEach((id) => window.clearTimeout(id));
    returnTimers.current.clear();
    letterRefs.current.forEach((el) => {
      el.style.transform = "";
      delete el.dataset.tx;
      delete el.dataset.ty;
      delete el.dataset.ts;
    });
  };


  /** Ponowne odtworzenie animacji składania (przycisk "odtwórz ponownie"). */
  const replay = () => {
    if (reducedMotion) return;
    setAssembled(false);
    clearMagnetics();
    setSettled(false);
    setRunKey((k) => k + 1);
  };

  // Rozproszenie -> scalenie, sterowane widocznością i licznikiem powtórzeń.
  useEffect(() => {
    if (reducedMotion) {
      setSettled(true);
      return;
    }
    if (!inView) {
      setAssembled(false);
      clearMagnetics();
      setSettled(false);
      return;
    }
    const total = visible.reduce((n, l) => n + l.length, 0);
    const start = requestAnimationFrame(() => requestAnimationFrame(() => setSettled(true)));
    const id = window.setTimeout(() => setAssembled(true), 800 + total * 34);
    return () => {
      cancelAnimationFrame(start);
      window.clearTimeout(id);
    };
  }, [inView, reducedMotion, visible, runKey]);



  // Pole przyciągania kursora + przeciąganie pojedynczych liter.
  useEffect(() => {
    if ((!assembled && !settled) || reducedMotion) return;
    const onMove = (e: PointerEvent) => {
      pointer.current = { x: e.clientX, y: e.clientY };
      const d = dragRef.current;
      if (d) {
        offsets.current.set(d.key, { x: d.ox + (e.clientX - d.px), y: d.oy + (e.clientY - d.py) });
      }
    };
    const onUp = () => {
      const d = dragRef.current;
      if (!d) return;
      dragRef.current = null;
      const cur = offsets.current.get(d.key) ?? { x: 0, y: 0 };
      offsets.current.delete(d.key);
      // Litera odczepia się i krąży w przestrzeni 3D, po czym wraca do napisu.
      orbits.current.set(d.key, {
        t0: performance.now(),
        sx: cur.x,
        sy: cur.y,
        rx: 0.4 + Math.random() * 0.9,
        ry: 0.6 + Math.random() * 1.4,
        rz: (Math.random() - 0.5) * 0.8,
        depth: 220 + Math.random() * 320,
        spin: 0.8 + Math.random() * 0.9,
        dir: Math.random() > 0.5 ? 1 : -1,
      });
    };
    const onLeave = () => {
      pointer.current = null;
    };
    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      const p = pointer.current;
      const held = dragRef.current?.key;
      const now = performance.now();
      letterRefs.current.forEach((el, key) => {
        // 1) Lot po orbicie 3D po odczepieniu
        const orb = orbits.current.get(key);
        if (orb) {
          const u = Math.min(1, (now - orb.t0) / ORBIT_MS);
          if (u >= 1) {
            orbits.current.delete(key);
            el.style.transform = "";
            el.dataset.tx = "0";
            el.dataset.ty = "0";
            el.dataset.ts = "1";
            return;
          }
          const env = Math.sin(Math.PI * u) ** 0.85;
          const ang = orb.dir * orb.spin * u * Math.PI * 2;
          const rad = 130 + orb.depth * 0.35;
          const x = orb.sx * (1 - u) + Math.cos(ang) * rad * env;
          const y = orb.sy * (1 - u) + Math.sin(ang) * rad * 0.5 * env;
          const z = orb.depth * env;
          el.style.transform =
            `perspective(900px) translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, ${z.toFixed(2)}px) ` +
            `rotateX(${(orb.rx * u * 360).toFixed(1)}deg) rotateY(${(orb.ry * u * 360).toFixed(1)}deg) ` +
            `rotateZ(${(orb.rz * u * 360).toFixed(1)}deg) scale(${(1 + 0.12 * env).toFixed(3)})`;
          el.dataset.tx = "0";
          el.dataset.ty = "0";
          el.dataset.ts = "1";
          return;
        }

        // 2) Przeciąganie / pole magnetyczne kursora
        const off = offsets.current.get(key);
        const r = el.getBoundingClientRect();
        let tx = off?.x ?? 0;
        let ty = off?.y ?? 0;
        let scale = off ? 1.12 : 1;
        if (!off && p) {
          const dx = p.x - (r.left + r.width / 2);
          const dy = p.y - (r.top + r.height / 2);
          const dist = Math.hypot(dx, dy);
          if (dist < FIELD && dist > 0.01) {
            const f = (1 - dist / FIELD) ** 2;
            tx = (dx / dist) * PULL * f;
            ty = (dy / dist) * PULL * f;
            scale = 1 + 0.16 * f;
          }
        }
        const prev = el.dataset.tx ? Number(el.dataset.tx) : 0;
        const prevY = el.dataset.ty ? Number(el.dataset.ty) : 0;
        const prevS = el.dataset.ts ? Number(el.dataset.ts) : 1;
        const ease = key === held ? 0.5 : 0.18;
        const nx = prev + (tx - prev) * ease;
        const ny = prevY + (ty - prevY) * ease;
        const ns = prevS + (scale - prevS) * 0.18;
        el.dataset.tx = String(nx);
        el.dataset.ty = String(ny);
        el.dataset.ts = String(ns);
        const tiltY = key === held ? nx * 0.12 : nx * 0.9;
        const tiltX = key === held ? -ny * 0.12 : -ny * 0.9;
        const z = key === held ? 80 : Math.abs(nx) + Math.abs(ny);
        el.style.transform =
          `perspective(900px) translate3d(${nx.toFixed(2)}px, ${ny.toFixed(2)}px, ${z.toFixed(2)}px) ` +
          `rotateY(${tiltY.toFixed(2)}deg) rotateX(${tiltX.toFixed(2)}deg) scale(${ns.toFixed(3)})`;

      });
    };

    const onMouseMove = (e: MouseEvent) => onMove(e as unknown as PointerEvent);
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    window.addEventListener("pointerleave", onLeave);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      window.removeEventListener("pointerleave", onLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [assembled, settled, reducedMotion]);

  const grabLetter = (key: string) => (e: React.PointerEvent) => {
    if (reducedMotion || (!assembled && !settled)) return;
    e.preventDefault();
    try {
      if (e.pointerId != null) (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    } catch {
      /* brak pointer capture - drag i tak obsługuje window */
    }
    orbits.current.delete(key);
    const t = returnTimers.current.get(key);
    if (t) {
      window.clearTimeout(t);
      returnTimers.current.delete(key);
    }
    const cur = offsets.current.get(key) ?? { x: 0, y: 0 };
    dragRef.current = { key, px: e.clientX, py: e.clientY, ox: cur.x, oy: cur.y };
    offsets.current.set(key, cur);
  };


  let globalIndex = 0;

  return (
    <div className="relative">
      <h1
        ref={ref}
        className={className}
        aria-label={visible.join(" ")}
        style={{ perspective: "1000px", transformStyle: "preserve-3d" }}
      >

        {visible.map((line, li) => (
          <span key={li} style={{ display: "block", whiteSpace: "nowrap" }} aria-hidden="true">
            {Array.from(line).map((ch, ci) => {
              const gi = globalIndex++;
              const key = `${li}-${ci}`;
              if (ch === " ") return <span key={key} style={{ display: "inline-block", width: "0.28em" }} />;
              const a = rand(gi) * Math.PI * 2;
              const dist = 220 + rand(gi + 7) * 340;
              const fx = Math.cos(a) * dist;
              const fy = Math.sin(a) * dist * 0.6;
              const rot = (rand(gi + 13) - 0.5) * 90;
              const isSettled = reducedMotion || settled;
              return (
                <span
                  key={key}
                  ref={(el) => {
                    if (el) letterRefs.current.set(key, el);
                    else letterRefs.current.delete(key);
                  }}
                  onPointerDown={grabLetter(key)}
                  onMouseDown={(e) => {
                    if (dragRef.current) return;
                    grabLetter(key)(e as unknown as React.PointerEvent);
                  }}
                  className={reducedMotion ? undefined : "letter-3d"}
                  style={{
                    display: "inline-block",
                    touchAction: reducedMotion ? undefined : "pan-y",
                    userSelect: "none",
                    transformStyle: "preserve-3d",
                    cursor: reducedMotion ? undefined : "grab",
                    willChange: "transform, opacity, filter",

                    opacity: isSettled ? 1 : 0,
                    filter: isSettled ? "blur(0px)" : "blur(6px)",
                    transform: isSettled
                      ? "translate3d(0,0,0)"
                      : `translate3d(${fx}px, ${fy}px, 0) rotate(${rot}deg) scale(0.6)`,
                    transition: reducedMotion
                      ? "none"
                      : assembled
                        ? "none"
                        : `transform 900ms cubic-bezier(0.16, 1, 0.3, 1) ${gi * 34}ms, opacity 500ms ease-out ${gi * 34}ms, filter 700ms ease-out ${gi * 34}ms`,
                  }}
                >
                  {ch}
                </span>
              );
            })}
          </span>
        ))}
        {srOnly ? <span className="sr-only">{srOnly}</span> : null}
      </h1>

      {!reducedMotion && (hint || replayLabel) && (
        <span className="mt-3 hidden md:inline-flex items-center gap-4 text-[10px] uppercase tracking-[0.28em]">
          {hint && (
            <span className="inline-flex items-center gap-2 text-white/40">
              <span className="w-6 h-px bg-lime-300/60" />
              {hint}
            </span>
          )}
          <button
            type="button"
            onClick={replay}
            className="inline-flex items-center gap-2 text-lime-300/80 hover:text-lime-200 transition-colors"
          >
            <span className="w-4 h-px bg-lime-300/60" />
            {replayLabel ?? "replay"}
          </button>
        </span>
      )}

    </div>
  );
}

export default AssemblingHeadline;
